/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authApi, getToken, removeToken } from '../../services/api';
import type { User, LoginRequest, SignupRequest, OTPRequest } from '../../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingEmail: string | null; // Email awaiting OTP verification
}

// Check if token exists in localStorage to restore auth state
const token = getToken();

const initialState: AuthState = {
  user: null,
  isAuthenticated: !!token,
  isLoading: false,
  error: null,
  pendingEmail: null,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Login failed');
    }
  }
);

export const signup = createAsyncThunk(
  'auth/signup',
  async (data: SignupRequest, { rejectWithValue }) => {
    try {
      const response = await authApi.signup(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Signup failed');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async (data: OTPRequest, { rejectWithValue }) => {
    try {
      const response = await authApi.verifyOTP(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'OTP verification failed');
    }
  }
);

export const resendOTP = createAsyncThunk(
  'auth/resendOTP',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await authApi.resendOTP(email);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to resend OTP');
    }
  }
);

export const requestPhoneOTP = createAsyncThunk(
  'auth/requestPhoneOTP',
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await authApi.requestPhoneOTP(phone);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to send OTP');
    }
  }
);

export const loginWithPhoneOTP = createAsyncThunk(
  'auth/loginWithPhoneOTP',
  async ({ phone, otp }: { phone: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.loginWithPhoneOTP(phone, otp);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Invalid or expired OTP');
    }
  }
);

export const socialLogin = createAsyncThunk(
  'auth/socialLogin',
  async (data: { provider: 'google' | 'apple'; token: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.socialLogin(data.provider, data.token);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Social login failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authApi.getCurrentUser();
      return user;
    } catch (error: any) {
      removeToken(); // Clear invalid/expired token
      return rejectWithValue(error.detail || 'Failed to get user');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authApi.logout();
});

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPendingEmail: (state, action: PayloadAction<string | null>) => {
      state.pendingEmail = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = action.payload.user || null;
      state.isAuthenticated = true;
      state.error = null;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Signup
    builder.addCase(signup.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(signup.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user || null;
      state.error = null;
    });
    builder.addCase(signup.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Verify OTP
    builder.addCase(verifyOTP.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(verifyOTP.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = action.payload.user ?? null;
      state.isAuthenticated = true;
      state.pendingEmail = null;
      state.error = null;
    });
    builder.addCase(verifyOTP.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Resend OTP
    builder.addCase(resendOTP.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(resendOTP.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(resendOTP.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Request phone OTP
    builder.addCase(requestPhoneOTP.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(requestPhoneOTP.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(requestPhoneOTP.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Login with phone OTP
    builder.addCase(loginWithPhoneOTP.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(loginWithPhoneOTP.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = action.payload.user || null;
      state.isAuthenticated = true;
      state.error = null;
    });
    builder.addCase(loginWithPhoneOTP.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Social login (Google/Apple)
    builder.addCase(socialLogin.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(socialLogin.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = action.payload.user || null;
      state.isAuthenticated = true;
      state.error = null;
    });
    builder.addCase(socialLogin.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get current user
    builder.addCase(getCurrentUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getCurrentUser.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = action.payload;
      state.isAuthenticated = true;
    });
    builder.addCase(getCurrentUser.rejected, (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
    });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.pendingEmail = null;
      state.error = null;
    });
  },
});

export const { clearError, setPendingEmail } = authSlice.actions;
export default authSlice.reducer;
